/**
 * Outgoing message assembly — the single place From / Reply-To / deliverability
 * headers are decided. The cron sender, the manual "process due sends" trigger,
 * and the test-send route all call buildOutgoing(), so a test reflects exactly
 * what a real send will look like.
 */

/** One line only — see headerSafe() in render.ts for why this matters. */
function headerSafe(s: string): string {
  return (s ?? '').replace(/[\r\n]+/g, ' ').trim()
}

export interface SendIdentity {
  /** the authenticated SMTP mailbox — this is the From ADDRESS, always */
  from_email: string
  /** default display name for this mailbox */
  from_name?: string | null
  /** default Reply-To for campaigns using this mailbox */
  reply_to?: string | null
}

export interface SendCampaign {
  /** overrides the identity's display name */
  from_name?: string | null
  /** overrides the identity's reply-to; where replies actually land */
  reply_to?: string | null
}

export interface OutgoingRecipient {
  email: string
  name?: string | null
}

export interface BuiltMessage {
  from: { name: string; email: string }
  to: { name?: string; email: string }
  replyTo?: { email: string }
  subject: string
  text: string
  html: string
  headers: Record<string, string>
  /** Envelope MAIL FROM / return-path. Always the authenticated mailbox. */
  envelopeFrom: string
}

/** "jess.doyle" -> "Jess Doyle" — last-resort display name. */
function displayFromEmail(email: string): string {
  const local = (email.split('@')[0] ?? '').replace(/[._-]+/g, ' ').trim()
  if (!local) return email
  return local
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * Resolution order for the display name: campaign override, then the mailbox
 * default, then a humanised local part.
 */
export function resolveFromName(identity: SendIdentity, campaign?: SendCampaign): string {
  return (
    campaign?.from_name?.trim() ||
    identity.from_name?.trim() ||
    displayFromEmail(identity.from_email)
  )
}

/** Campaign reply-to wins, then the mailbox default, else none. */
export function resolveReplyTo(
  identity: SendIdentity,
  campaign?: SendCampaign,
): string | undefined {
  return campaign?.reply_to?.trim() || identity.reply_to?.trim() || undefined
}

/**
 * Builds one outgoing message.
 *
 * IMPORTANT — the From ADDRESS is always identity.from_email, the mailbox we
 * actually authenticated as (or one of its verified aliases). It must never be
 * an arbitrary address: Gmail and most providers will rewrite the header, break
 * DKIM/SPF alignment, or reject the message outright. A different reply
 * destination is expressed with Reply-To, never by forging From. For the same
 * reason envelopeFrom (MAIL FROM / return-path) stays on the authenticated
 * mailbox so SPF continues to align.
 */
export function buildOutgoing(args: {
  identity: SendIdentity
  campaign?: SendCampaign
  to: OutgoingRecipient
  subject: string
  text: string
  html: string
  /** signed one-click unsubscribe URL */
  unsubscribeUrl?: string
  /** prefixes the subject, e.g. "[Test] " */
  subjectPrefix?: string
  /** injected for deterministic tests */
  now?: Date
  messageId?: string
}): BuiltMessage {
  const { identity, campaign, to, unsubscribeUrl } = args
  const now = args.now ?? new Date()

  // Defence in depth: these come from user-editable campaign/identity fields,
  // and they end up in headers.
  const fromName = headerSafe(resolveFromName(identity, campaign))
  const replyTo = resolveReplyTo(identity, campaign)?.replace(/[\r\n\s]/g, '')

  const domain = identity.from_email.split('@')[1] ?? 'localhost'
  const messageId = args.messageId ?? `<${crypto.randomUUID()}@${domain}>`

  const headers: Record<string, string> = {
    // Explicit Date and Message-ID: some SMTP relays will add them, but a
    // missing or relay-generated one is a spam signal for bulk Gmail.
    Date: now.toUTCString(),
    'Message-ID': messageId,
  }

  if (unsubscribeUrl) {
    // RFC 8058 one-click. The mailto goes to the monitored inbox — the reply-to
    // when there is one, otherwise the sending mailbox.
    const mailbox = replyTo ?? identity.from_email
    headers['List-Unsubscribe'] =
      `<${unsubscribeUrl}>, <mailto:${mailbox}?subject=unsubscribe>`
    headers['List-Unsubscribe-Post'] = 'List-Unsubscribe=One-Click'
  }

  return {
    from: { name: fromName, email: identity.from_email },
    to: to.name ? { name: to.name, email: to.email } : { email: to.email },
    ...(replyTo ? { replyTo: { email: replyTo } } : {}),
    subject: headerSafe(`${args.subjectPrefix ?? ''}${args.subject}`),
    text: args.text,
    html: args.html,
    headers,
    envelopeFrom: identity.from_email,
  }
}

/** Human-readable header block — used by dry-run logs and the UI preview. */
export function describeHeaders(m: BuiltMessage): string {
  const lines = [
    `From: "${m.from.name}" <${m.from.email}>`,
    `To: ${m.to.name ? `"${m.to.name}" <${m.to.email}>` : m.to.email}`,
  ]
  if (m.replyTo) lines.push(`Reply-To: <${m.replyTo.email}>`)
  lines.push(`Subject: ${m.subject}`)
  for (const [k, v] of Object.entries(m.headers)) lines.push(`${k}: ${v}`)
  lines.push(`(envelope MAIL FROM: ${m.envelopeFrom})`)
  return lines.join('\n')
}
